/**
 * @module ExternalTestsCatalog/ExternalTestCatalogRecentRunsCell
 *
 * Ten-run health strip for one catalog row. Reuses the same segment mapping
 * and pass-rate math as the item detail page's
 * {@link ../ExternalTestItemDetail/externalRunHistory}, just without an
 * active run to highlight or a click-to-navigate handler (the row itself is
 * already a link).
 */
import React from 'react';
import { Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type { ExternalTestHealth } from '@chatsdet/types';
import { WORKFLOW_HEALTH_META } from '../flow-list/components/cells';
import { RunResultBars, type RunBarSegment } from '../../components/RunResultBars';
import {
  displayLabel,
  externalHistoryPassRate,
  externalHistoryRunResult,
} from '../ExternalTestItemDetail/externalRunHistory';
import { formatShortDate } from '../../../lib/formatShortDate';
import type { ExternalTestCatalogRecentRun } from '../../../features/external-tests/useExternalTestCatalog';

export function ExternalTestCatalogRecentRunsCell({
  runs,
  health,
  isLoading,
  isError,
}: {
  runs: ExternalTestCatalogRecentRun[] | undefined;
  health: ExternalTestHealth;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isError) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const pct = runs ? externalHistoryPassRate(runs) : null;
  const segments: RunBarSegment[] = (runs ?? [])
    .slice(0, 10)
    .reverse()
    .map((run) => {
      const label = displayLabel(run);
      return {
        id: run.runId,
        result: externalHistoryRunResult(run),
        isActive: false,
        ariaLabel: label,
        tooltip: `${label} · ${formatShortDate(run.at)}`,
      };
    });
  const meta = WORKFLOW_HEALTH_META[health];

  return (
    <RunResultBars
      segments={isLoading ? [] : segments}
      slots={10}
      trailing={
        <>
          {meta && (
            <TooltipTrigger delay={0} offset={8} placement="top">
              <span className="inline-flex cursor-default" aria-label={`Health: ${meta.label}`}>
                <meta.Icon size={14} weight="fill" style={{ color: meta.color }} />
              </span>
              <Tooltip variant="light" className="max-w-xs px-3 py-2 text-left">
                <div className="font-medium">{meta.label}</div>
                <div className="text-muted-foreground">{meta.description}</div>
              </Tooltip>
            </TooltipTrigger>
          )}
          {pct !== null && (
            <span
              className={`font-mono text-[11px] tabular-nums ${
                pct < 50 ? 'font-medium text-destructive' : 'text-muted-foreground'
              }`}
            >
              {pct}%
            </span>
          )}
        </>
      }
    />
  );
}
